import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/data/products";
import { whyPoints, steps } from "@/data/content";
import { useLeadForm } from "@/components/lead/LeadForm";
import { SEO, breadcrumbLd } from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { CTASection } from "@/components/site/CTASection";

export default function WhyChooseUs() {
  const { openForm } = useLeadForm();
  return (
    <>
      <SEO
        title="Why Choose My Blinds Company | Blinds Measured & Fitted in Birmingham"
        description="Why choose My Blinds Company over ordering blinds online? Personal advice at home, professional measuring, made-to-measure quality, expert fitting and local aftercare across Birmingham."
        path="/why-choose-us"
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Why Choose Us", path: "/why-choose-us" },
          ]),
        ]}
      />
      <section className="bg-warm-50 grain py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <Eyebrow className="justify-center">Why Choose Us</Eyebrow>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-slate-900 tracking-tight leading-tight">
              The difference is doing it properly
            </h1>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-600">
              Anyone can sell you a blind. We take care of the whole process —
              advice, measuring, manufacture, fitting and aftercare — so what
              arrives at your window is exactly right.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 gap-6">
            {whyPoints.map((w, i) => (
              <Reveal key={w.title} delay={(i % 2) * 0.08}>
                <div className="h-full rounded-2xl border border-warm-200 bg-white p-8 shadow-sm hover:shadow-lg hover:border-navy-800/20 transition-all duration-300">
                  <span className="font-serif italic text-3xl text-warm-300">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h2 className="mt-2 font-sans text-lg font-semibold text-slate-900">{w.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-navy-950 grain text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] aspect-[4/3] shadow-2xl">
              <img
                src={IMAGES.livingRoom}
                alt="Living room with made-to-measure blinds fitted by My Blinds Company"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow className="text-white/50">Versus Ordering Online</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight leading-tight">
              What you avoid by booking a home visit
            </h2>
            <ul className="mt-6 space-y-4 text-sm sm:text-base text-white/70 leading-relaxed">
              <li>— Measuring mistakes that make a made-to-measure order worthless.</li>
              <li>— Choosing fabric from a screen and finding the colour is wrong in your light.</li>
              <li>— Drilling, ladders and fitting it yourself at the weekend.</li>
              <li>— Having no one to call when something needs adjusting.</li>
            </ul>
            <button
              type="button"
              data-testid="why-choose-book-btn"
              onClick={() => openForm({ source: "why_choose_page" })}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-navy-900 shadow-xl transition-all duration-300 hover:-translate-y-0.5"
            >
              Book a Free Design Visit <ArrowRight className="h-4 w-4" />
            </button>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-12">
            <Eyebrow>The Process</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              One team, start to finish
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div>
                  <span className="font-serif italic text-5xl text-warm-300">{s.n}</span>
                  <h3 className="mt-3 font-sans text-lg font-semibold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection source="why_choose_final_cta" />
    </>
  );
}
